import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { FormControlComponent } from 'design/formControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { useRewardForm } from 'hooks/useRewardForm';
import { validateRewardFormValues } from 'utils/rewardFormUtils';
import { RewardFieldset } from 'containers/RewardFieldset';
import { useServices } from 'hooks/useServices';
import { RewardAmountExceedsTheBalance } from 'utils/errors';
import { RewardFormValues } from 'types/rewardFormTypes';
import { PartnerFinanceMonthRewardIncome } from 'types/partnerFinance';

type Props = {
  partnerId: number;
  editedRewardIncome?: PartnerFinanceMonthRewardIncome;
  onCancel(): void;
  onEdited(): void;
};

export const PartnerManagerRewardEditor = (props: Props) => {
  const { partnerId, editedRewardIncome, onCancel: cancel, onEdited } = props;

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const { createPartnerReward } = useServices();

  const initialRewardValues = React.useMemo<RewardFormValues>(
    () => ({
      partnerId,
      rewardId: editedRewardIncome?.id || null,
      amount: editedRewardIncome?.amount
        ? editedRewardIncome.amount.toString()
        : '',
      comment: editedRewardIncome?.comment || '',
    }),
    [
      editedRewardIncome?.amount,
      editedRewardIncome?.comment,
      editedRewardIncome?.id,
      partnerId,
    ]
  );

  const {
    values,
    violations,
    setViolations,
    setAmountValue,
    setCommentValue,
  } = useRewardForm(initialRewardValues);

  const {
    partnerManager: { rewardEditorScreen: ownDictionary },
  } = useIntlDictionary();

  const submit = React.useCallback(() => {
    setViolations([]);

    const violations = validateRewardFormValues(values);

    if (violations.length > 0) {
      Promise.resolve(violations).then(setViolations);
    } else {
      const promise = createPartnerReward(values)
        .then(() => {
          onEdited();
        })
        .catch((error) => {
          if (error instanceof RewardAmountExceedsTheBalance) {
            setViolations([
              {
                name: 'AmountExceedsBalance',
              },
            ]);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [createPartnerReward, injectPromise, onEdited, setViolations, values]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={violations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            <RewardFieldset
              disabled={asyncActive}
              values={values}
              violations={violations}
              onAmountValueChange={setAmountValue}
              onCommentValueChange={setCommentValue}
            />
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
